import { api } from '@/lib/axios';

export type ChantierStatut = 'EN_PREPARATION' | 'EN_COURS' | 'SUSPENDU' | 'ACHEVE';
export type BudgetPoste = 'MAIN_OEUVRE' | 'MATERIAUX' | 'MATERIEL' | 'SOUS_TRAITANCE' | 'FRAIS_GENERAUX' | 'IMPREVU';
export type PrioriteChantier = 'BASSE' | 'NORMALE' | 'HAUTE' | 'CRITIQUE';
export type TypeChantier = 'TRAVAUX_PUBLICS' | 'BATIMENT' | 'HYDRAULIQUE' | 'AUTRE';

export const PRIORITES: PrioriteChantier[] = ['BASSE', 'NORMALE', 'HAUTE', 'CRITIQUE'];
export const TYPES_CHANTIER: TypeChantier[] = ['TRAVAUX_PUBLICS', 'BATIMENT', 'HYDRAULIQUE', 'AUTRE'];
/** Libellé complet du type de chantier (formulaires, fiche). */
export const TYPE_CHANTIER_LABEL: Record<TypeChantier, string> = {
  TRAVAUX_PUBLICS: 'Travaux Publics', BATIMENT: 'Bâtiment', HYDRAULIQUE: 'Hydraulique', AUTRE: 'Autre',
};
/** Libellé court pour le badge discret des cartes. */
export const TYPE_CHANTIER_BADGE: Record<TypeChantier, string> = {
  TRAVAUX_PUBLICS: 'TP', BATIMENT: 'Bât.', HYDRAULIQUE: 'Hydr.', AUTRE: 'Autre',
};

export interface ChantierDTO {
  id: string;
  code: string;
  nom: string;
  type_chantier: TypeChantier | null;
  marche_id: string | null;
  numero_marche: string | null;
  client_id: string | null;
  chef_projet_id: string | null;
  conducteur_travaux: string | null;
  localisation: string | null;
  wilaya_id: number | null;
  priorite: PrioriteChantier;
  surface_m2: number | null;
  lineaire_ml: number | null;
  budget_materiaux: number | null;
  date_debut_prevue: string | null;
  date_fin_prevue: string | null;
  date_debut_reelle: string | null;
  date_fin_reelle: string | null;
  delai_contractuel_jours: number | null;
  statut: ChantierStatut;
  taux_avancement: number;
  photo_url: string | null;
  analyse: string | null;
  notes: string | null;
  is_archived: boolean;
}

export interface ChantierInput {
  code: string;
  nom: string;
  type_chantier?: TypeChantier | null;
  numero_marche?: string | null;
  client_id?: string | null;
  conducteur_travaux?: string | null;
  localisation?: string | null;
  wilaya_id?: number | null;
  priorite?: PrioriteChantier;
  surface_m2?: number | null;
  lineaire_ml?: number | null;
  budget_materiaux?: number | null;
  statut: ChantierStatut;
  taux_avancement: number;
  date_debut_prevue?: string | null;
  date_debut_reelle?: string | null;
  date_fin_prevue?: string | null;
  delai_contractuel_jours?: number | null;
  analyse?: string | null;
  notes?: string | null;
  is_archived?: boolean;
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ControleCoutLigne {
  poste: string;
  prevu: number;
  engage: number;
  realise: number;
  ecart: number;
  ecart_pct: number;
  depassement: boolean;
}

export interface ControleCout {
  chantier_id: string;
  lignes: ControleCoutLigne[];
  total_prevu: number;
  total_realise: number;
  cout_reel_total: number;
  cout_comptabilise: number;
  ecart_comptabilisation: number;
  total_engage: number;
  reste_a_engager: number;
  // Composition du réalisé (transparence).
  realise_depenses: number;
  realise_factures: number;
  realise_paie: number;
  realise_sous_traitance: number;
  materiaux_consommes_stock: number;
}

export interface BudgetLigne {
  id?: string;
  poste: BudgetPoste;
  montant_prevu: number;
  montant_engage: number;
  montant_realise: number;
}

export async function listChantiers(params?: { q?: string; statut?: ChantierStatut; archives?: boolean }): Promise<Page<ChantierDTO>> {
  const { data } = await api.get<Page<ChantierDTO>>('/chantiers', { params });
  return data;
}

export async function deleteChantier(id: string): Promise<void> {
  await api.delete(`/chantiers/${id}`);
}

export async function getChantier(id: string): Promise<ChantierDTO> {
  const { data } = await api.get<ChantierDTO>(`/chantiers/${id}`);
  return data;
}

export async function createChantier(payload: ChantierInput): Promise<ChantierDTO> {
  const { data } = await api.post<ChantierDTO>('/chantiers', payload);
  return data;
}

export async function updateChantier(id: string, payload: Partial<ChantierInput>): Promise<ChantierDTO> {
  const { data } = await api.put<ChantierDTO>(`/chantiers/${id}`, payload);
  return data;
}

export async function getControleCouts(id: string): Promise<ControleCout> {
  const { data } = await api.get<ControleCout>(`/chantiers/${id}/controle-couts`);
  return data;
}

export async function getBudget(id: string): Promise<BudgetLigne[]> {
  const { data } = await api.get<BudgetLigne[]>(`/chantiers/${id}/budget`);
  return data;
}

export async function setBudget(id: string, lignes: Omit<BudgetLigne, 'id' | 'montant_realise'>[]): Promise<BudgetLigne[]> {
  const { data } = await api.put<BudgetLigne[]>(`/chantiers/${id}/budget`, lignes);
  return data;
}

/* -------------------------------- Planning -------------------------------- */
export type StatutPlanning = 'A_FAIRE' | 'EN_COURS' | 'EN_REVISION' | 'TERMINE';

export interface PlanningTache {
  id: string;
  chantier_id: string;
  tache: string;
  date_debut_prevue: string | null;
  date_fin_prevue: string | null;
  date_debut_reelle: string | null;
  date_fin_reelle: string | null;
  avancement_pct: number;
  statut: StatutPlanning;
  responsable_id: string | null;
  responsable_nom: string | null;
}

interface PlanningCreatePayload {
  tache: string;
  date_debut_prevue?: string | null;
  date_fin_prevue?: string | null;
  avancement_pct?: number;
  statut?: StatutPlanning;
  responsable_id?: string | null;
}
interface PlanningUpdatePayload {
  tache?: string;
  date_debut_prevue?: string | null;
  date_fin_prevue?: string | null;
  date_debut_reelle?: string | null;
  date_fin_reelle?: string | null;
  avancement_pct?: number;
  statut?: StatutPlanning;
  responsable_id?: string | null;
}

export async function getPlanning(chantierId: string): Promise<PlanningTache[]> {
  const { data } = await api.get<PlanningTache[]>(`/chantiers/${chantierId}/planning`);
  return data;
}
export async function addPlanningTache(chantierId: string, payload: PlanningCreatePayload): Promise<PlanningTache> {
  const { data } = await api.post<PlanningTache>(`/chantiers/${chantierId}/planning`, payload);
  return data;
}
export async function updatePlanningTache(chantierId: string, tacheId: string, payload: PlanningUpdatePayload): Promise<PlanningTache> {
  const { data } = await api.put<PlanningTache>(`/chantiers/${chantierId}/planning/${tacheId}`, payload);
  return data;
}
export async function deletePlanningTache(chantierId: string, tacheId: string): Promise<void> {
  await api.delete(`/chantiers/${chantierId}/planning/${tacheId}`);
}

/* ----------------- Contrôles, actions correctives, bilan ------------------ */
export type ControleDomaine = 'QUALITE' | 'SECURITE' | 'DELAI' | 'CONFORMITE' | 'ENVIRONNEMENT' | 'AUTRE';
export type ControleStatut = 'CONFORME' | 'NON_CONFORME' | 'A_VERIFIER';
export type ActionStatut = 'OUVERTE' | 'EN_COURS' | 'CLOTUREE';

export interface ControleDTO {
  id: string;
  chantier_id: string;
  date_controle: string | null;
  domaine: ControleDomaine;
  libelle: string;
  statut: ControleStatut;
  observations: string | null;
  controleur: string | null;
}

export interface ActionCorrectiveDTO {
  id: string;
  chantier_id: string;
  controle_id: string | null;
  description: string;
  responsable: string | null;
  date_echeance: string | null;
  priorite: PrioriteChantier;
  statut: ActionStatut;
  date_cloture: string | null;
}

export interface BilanChantierDTO {
  taux_avancement: number;
  budget_prevu: number;
  cout_reel: number;
  cout_comptabilise: number;
  ecart_budget: number;
  nb_situations: number;
  montant_situations: number;
  nb_controles: number;
  nb_non_conformes: number;
  nb_actions_ouvertes: number;
  budget_materiaux: number;
  achats_engage: number;
  achats_realise: number;
  materiaux_consommes: number;
}

export const CONTROLE_DOMAINES: ControleDomaine[] = ['QUALITE', 'SECURITE', 'DELAI', 'CONFORMITE', 'ENVIRONNEMENT', 'AUTRE'];

export async function listControles(chantierId: string): Promise<ControleDTO[]> {
  const { data } = await api.get<ControleDTO[]>(`/chantiers/${chantierId}/controles`);
  return data;
}
export async function addControle(chantierId: string, payload: { date_controle?: string | null; domaine: ControleDomaine; libelle: string; statut: ControleStatut; observations?: string | null; controleur?: string | null }): Promise<ControleDTO> {
  const { data } = await api.post<ControleDTO>(`/chantiers/${chantierId}/controles`, payload);
  return data;
}
export async function deleteControle(chantierId: string, id: string): Promise<void> {
  await api.delete(`/chantiers/${chantierId}/controles/${id}`);
}

export async function listActions(chantierId: string): Promise<ActionCorrectiveDTO[]> {
  const { data } = await api.get<ActionCorrectiveDTO[]>(`/chantiers/${chantierId}/actions-correctives`);
  return data;
}
export async function addAction(chantierId: string, payload: { description: string; responsable?: string | null; date_echeance?: string | null; priorite: PrioriteChantier; statut: ActionStatut; controle_id?: string | null }): Promise<ActionCorrectiveDTO> {
  const { data } = await api.post<ActionCorrectiveDTO>(`/chantiers/${chantierId}/actions-correctives`, payload);
  return data;
}
export async function updateAction(chantierId: string, id: string, payload: { statut?: ActionStatut; responsable?: string | null; date_echeance?: string | null }): Promise<ActionCorrectiveDTO> {
  const { data } = await api.put<ActionCorrectiveDTO>(`/chantiers/${chantierId}/actions-correctives/${id}`, payload);
  return data;
}
export async function deleteAction(chantierId: string, id: string): Promise<void> {
  await api.delete(`/chantiers/${chantierId}/actions-correctives/${id}`);
}

export async function getBilanChantier(chantierId: string): Promise<BilanChantierDTO> {
  const { data } = await api.get<BilanChantierDTO>(`/chantiers/${chantierId}/bilan`);
  return data;
}

/* ============================ Espace TERRAIN ============================== */
/* Journal de chantier, photos d'avancement, attachements (quantités sans prix). */

export interface JournalDTO {
  id: string;
  chantier_id: string;
  date_journal: string;
  meteo: string | null;
  temperature: number | null;
  effectif_present: number | null;
  evenements: string | null;
  incidents: string | null;
  redige_par: string | null;
}
export interface JournalInput {
  date_journal: string;
  meteo?: string | null;
  temperature?: number | null;
  effectif_present?: number | null;
  evenements?: string | null;
  incidents?: string | null;
  redige_par?: string | null;
}

export async function listJournal(chantierId: string): Promise<JournalDTO[]> {
  const { data } = await api.get<JournalDTO[]>(`/chantiers/${chantierId}/journal`);
  return data;
}
export async function addJournal(chantierId: string, payload: JournalInput): Promise<JournalDTO> {
  const { data } = await api.post<JournalDTO>(`/chantiers/${chantierId}/journal`, payload);
  return data;
}
export async function deleteJournal(chantierId: string, id: string): Promise<void> {
  await api.delete(`/chantiers/${chantierId}/journal/${id}`);
}

export interface PhotoDTO {
  id: string;
  chantier_id: string;
  url: string;
  legende: string | null;
  date_prise: string | null;
}
export async function listPhotos(chantierId: string): Promise<PhotoDTO[]> {
  const { data } = await api.get<PhotoDTO[]>(`/chantiers/${chantierId}/photos`);
  return data;
}
export async function addPhoto(chantierId: string, payload: { url: string; legende?: string | null; date_prise?: string | null }): Promise<PhotoDTO> {
  const { data } = await api.post<PhotoDTO>(`/chantiers/${chantierId}/photos`, payload);
  return data;
}
export async function deletePhoto(chantierId: string, id: string): Promise<void> {
  await api.delete(`/chantiers/${chantierId}/photos/${id}`);
}

export interface LigneAttachementDTO {
  id: string;
  designation: string;
  unite: string | null;
  quantite: number;
  ordre: number;
}
export interface AttachementDTO {
  id: string;
  chantier_id: string;
  numero: number;
  statut: string; // BROUILLON | VALIDE
  date_attachement: string | null;
  periode_mois: number | null;
  periode_annee: number | null;
  objet: string | null;
  observations: string | null;
  lignes: LigneAttachementDTO[];
}
export interface LigneAttachementInput {
  designation: string;
  unite?: string | null;
  quantite: number;
  ordre?: number;
}
export interface AttachementInput {
  date_attachement?: string | null;
  periode_mois?: number | null;
  periode_annee?: number | null;
  objet?: string | null;
  observations?: string | null;
  lignes: LigneAttachementInput[];
}

export async function listAttachements(chantierId: string): Promise<AttachementDTO[]> {
  const { data } = await api.get<AttachementDTO[]>(`/chantiers/${chantierId}/attachements`);
  return data;
}
export async function addAttachement(chantierId: string, payload: AttachementInput): Promise<AttachementDTO> {
  const { data } = await api.post<AttachementDTO>(`/chantiers/${chantierId}/attachements`, payload);
  return data;
}
export async function updateAttachement(chantierId: string, id: string, payload: Partial<AttachementInput>): Promise<AttachementDTO> {
  const { data } = await api.put<AttachementDTO>(`/chantiers/${chantierId}/attachements/${id}`, payload);
  return data;
}
export async function validerAttachement(chantierId: string, id: string): Promise<AttachementDTO> {
  const { data } = await api.post<AttachementDTO>(`/chantiers/${chantierId}/attachements/${id}/valider`);
  return data;
}
export async function deleteAttachement(chantierId: string, id: string): Promise<void> {
  await api.delete(`/chantiers/${chantierId}/attachements/${id}`);
}

/* --------------------------- Réception & PV ------------------------------ */
export interface ReserveDTO {
  id: string;
  description: string;
  levee: boolean;
  date_levee: string | null;
  ordre: number;
}
export interface PVDTO {
  id: string;
  chantier_id: string;
  statut: string; // OUVERT | CLOTURE
  type: string; // PROVISOIRE | DEFINITIVE
  date_pv: string | null;
  objet: string | null;
  intervenants: string | null;
  observations: string | null;
  reserves: ReserveDTO[];
}
export interface ReserveInput {
  description: string;
  levee?: boolean;
  date_levee?: string | null;
  ordre?: number;
}
export interface PVInput {
  type?: string;
  date_pv?: string | null;
  objet?: string | null;
  intervenants?: string | null;
  observations?: string | null;
  reserves?: ReserveInput[];
}

export async function listReceptionsPV(chantierId: string): Promise<PVDTO[]> {
  const { data } = await api.get<PVDTO[]>(`/chantiers/${chantierId}/receptions-pv`);
  return data;
}
export async function addReceptionPV(chantierId: string, payload: PVInput): Promise<PVDTO> {
  const { data } = await api.post<PVDTO>(`/chantiers/${chantierId}/receptions-pv`, payload);
  return data;
}
export async function updateReceptionPV(chantierId: string, id: string, payload: PVInput): Promise<PVDTO> {
  const { data } = await api.put<PVDTO>(`/chantiers/${chantierId}/receptions-pv/${id}`, payload);
  return data;
}
export async function basculerReserve(chantierId: string, pvId: string, reserveId: string): Promise<PVDTO> {
  const { data } = await api.post<PVDTO>(`/chantiers/${chantierId}/receptions-pv/${pvId}/reserves/${reserveId}/basculer`);
  return data;
}
export async function deleteReceptionPV(chantierId: string, id: string): Promise<void> {
  await api.delete(`/chantiers/${chantierId}/receptions-pv/${id}`);
}
